import { Component, type ReactNode, type ErrorInfo } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught error:", error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <span style={styles.icon}>&#9888;</span>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              {this.state.error?.message ?? "An unexpected error occurred."}
            </p>
            <button onClick={this.handleReload} style={styles.reloadBtn}>
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "var(--comp-canvas)",
    padding: "var(--space-5)",
  },
  card: {
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-7) var(--space-7)",
    textAlign: "center",
    maxWidth: 420,
    width: "100%",
  },
  icon: {
    fontSize: 24,
    color: "var(--comp-mustard)",
    display: "block",
    marginBottom: 16,
  },
  title: {
    fontFamily: "var(--font-mono)",
    fontSize: 16,
    fontWeight: 600,
    color: "var(--fg-1)",
    marginBottom: 8,
  },
  message: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--fg-3)",
    lineHeight: 1.5,
    marginBottom: 24,
    wordBreak: "break-word",
  },
  reloadBtn: {
    display: "inline-block",
    padding: "var(--space-3) var(--space-5)",
    background: "var(--comp-ink)",
    color: "var(--fg-on-deep)",
    border: "none",
    borderRadius: "var(--radius-md)",
    fontFamily: "var(--font-mono)",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
  },
};
